/**
 * Economy System - handles gold income from territory and buildings
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { FactionComponent } from '@/game/components/Faction';
import { type BuildingComponent, getBuildingEfficiency } from '@/game/components/Building';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { TerritorySystem } from './TerritorySystem';
import { EventBus } from '@/core/events/EventBus';
import { BUILDING_STATS, FACTIONS } from '@/game/factions/FactionData';
import type { FactionId } from '@/types';

export class EconomySystem implements System {
  private factionManager: FactionManager;
  private territorySystem: TerritorySystem;
  private incomeInterval: number = 5.0; // Income every 5 seconds (faster feedback)
  private timeSinceIncome: number = 0;
  private countUpdateInterval: number = 1.0; // Update counts every second
  private timeSinceCountUpdate: number = 1.0; // Start at interval to update immediately on first frame
  private baseIncomePerTerritory: number = 0.1; // Greatly reduced - territory mainly for strategic value
  private capitalIncome: number = 17; // ~50 per 15s -> 17 per 5s

  constructor(factionManager: FactionManager, territorySystem: TerritorySystem) {
    this.factionManager = factionManager;
    this.territorySystem = territorySystem;
  }

  update(world: World, deltaTime: number): void {
    this.timeSinceIncome += deltaTime;
    this.timeSinceCountUpdate += deltaTime;

    // Update faction counts frequently (every second)
    if (this.timeSinceCountUpdate >= this.countUpdateInterval) {
      this.timeSinceCountUpdate = 0;
      this.updateFactionCounts(world);
    }

    if (this.timeSinceIncome < this.incomeInterval) {
      return;
    }

    this.timeSinceIncome = 0;

    // Calculate income for each faction
    const factionIncome = new Map<FactionId, number>();

    // Territory income
    const territoryCounts = this.territorySystem.countTerritoryByFaction();
    for (const [factionId, count] of territoryCounts) {
      const faction = FACTIONS[factionId];
      const territoryIncome = count * this.baseIncomePerTerritory * (faction?.incomeBonus ?? 1);
      factionIncome.set(factionId, (factionIncome.get(factionId) ?? 0) + territoryIncome);
    }

    // Building income
    const buildings = world.query([ComponentTypes.Building, ComponentTypes.Faction]);

    for (const entity of buildings) {
      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

      if (!building.isConstructed) continue;

      const stats = BUILDING_STATS[building.buildingType];
      if (!stats || !stats.income) continue;

      let income = stats.income;

      // Capital provides base income
      if (building.buildingType === 'capital') {
        income = this.capitalIncome;
      }

      // Apply building level efficiency bonus
      income *= getBuildingEfficiency(building.level);

      // Apply faction bonus
      const factionData = FACTIONS[faction.factionId];
      if (factionData) {
        income *= factionData.incomeBonus;
      }

      factionIncome.set(faction.factionId, (factionIncome.get(faction.factionId) ?? 0) + income);
    }

    // Apply income to factions
    for (const [factionId, income] of factionIncome) {
      this.factionManager.addGold(factionId, Math.floor(income));

      EventBus.emit('economy:income', {
        factionId,
        amount: Math.floor(income),
      });
    }
  }

  private updateFactionCounts(world: World): void {
    const territoryCounts = this.territorySystem.countTerritoryByFaction();

    // Count units per faction
    const unitCounts = new Map<FactionId, number>();
    const units = world.query([ComponentTypes.Faction, ComponentTypes.Unit]);
    for (const entity of units) {
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      unitCounts.set(faction.factionId, (unitCounts.get(faction.factionId) ?? 0) + 1);
    }

    // Count buildings per faction
    const buildingCounts = new Map<FactionId, number>();
    const buildings = world.query([ComponentTypes.Faction, ComponentTypes.Building]);
    for (const entity of buildings) {
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      buildingCounts.set(faction.factionId, (buildingCounts.get(faction.factionId) ?? 0) + 1);
    }

    // Update faction manager
    for (const factionId of Object.keys(FACTIONS) as FactionId[]) {
      this.factionManager.updateCounts(
        factionId,
        unitCounts.get(factionId) ?? 0,
        buildingCounts.get(factionId) ?? 0,
        territoryCounts.get(factionId) ?? 0
      );
    }
  }
}
